// ─────────────────────────────────────────────────────────────────────────────
// Trade Review guide page
//
// Walks the user through the post-trade feedback surface:
//   - Recent trade picker
//   - Trade summary card (net P&L, prices, discipline, behavioral tags)
//   - Execution Scorecard (sliders vs personal baseline)
//   - Coaching notes (pattern-aware commentary)
//   - User's own notes (free-text from Trade Entry reflection)
//
// IP-safe: describes what each component shows behaviorally, not how the
// discipline score or coaching notes are computed.
// ─────────────────────────────────────────────────────────────────────────────

import {
  GuideLayout, H2First, H2, P, UL, LI, UI,
  ScreenshotFrame, Callout,
} from "../GuideLayout";

export function GuideTradeReview() {
  return (
    <GuideLayout
      title="Trade Review"
      subtitle="Get coaching feedback on a single trade — a discipline score, behavioral tags, and short notes comparing this trade to your own account baseline."
      prev={{ slug: "can-i-trade", label: "Can I Trade?" }}
      next={{ slug: "journal",     label: "Journal" }}
    >
      <H2First>What it's for</H2First>
      <P>
        Trade Entry is where you record what happened. Trade Review is
        where you find out what it <em>meant</em>. Pick any recent trade
        and the page surfaces a discipline score, the behavioral context
        flags the detectors picked up, an execution scorecard that
        compares this trade to your own personal baseline, and short
        coaching notes that connect this trade to patterns in your
        broader history.
      </P>
      <P>
        It's the post-trade companion to Trade Entry — short read, every
        time, after notable trades.
      </P>

      <H2>Picking a trade</H2>
      <P>
        At the top of the page is a <UI>Viewing Account</UI> selector
        followed by a <UI>Recent Trades — Select to Review</UI> list:
        your last 20 trades for that account, sorted in the order your
        broker reported them. Click any row to load that trade into the
        review surface below. The list shows date, symbol, direction,
        setup, and net P&L per row — enough to find a specific trade
        you want to revisit.
      </P>
      <P>
        Above the list, a <UI>Gross</UI> / <UI>Net</UI> toggle controls
        how P&L is displayed throughout the page. Gross is the raw trade
        result; Net subtracts your logged commissions and fees.
      </P>

      <ScreenshotFrame
        src="/guide/trade-review-overview.png"
        alt="Trade Review with selected trade and execution scorecard"
        caption="Trade Review pulls a single trade and surrounds it with behavioral context."
      />

      <H2>The trade summary card</H2>
      <P>
        Below the trade list is a summary card for whichever trade you've
        selected:
      </P>
      <UL>
        <LI>Symbol, direction, date, and entry time at the top</LI>
        <LI>Net P&L (or Gross, depending on your toggle) in large text</LI>
        <LI>Entry → Exit prices and contract count on the line below</LI>
        <LI><UI>Discipline</UI> score for this trade, on the right side</LI>
        <LI>Behavioral tags beneath — pills like <UI>VWAP Bounce</UI>, <UI>take profit hit</UI>, <UI>FOMO</UI>, <UI>Choppy</UI></LI>
      </UL>

      <P>
        The <strong>Discipline</strong> score is a single number that
        captures how cleanly you executed this trade against your own
        rules and ratings. It's not pass/fail — it's a 1–10 read that
        rolls up into your Analytics tab over time.
      </P>

      <P>
        The <strong>behavioral tags</strong> beneath the score are a mix
        of two things:
      </P>
      <UL>
        <LI><strong>Self-logged labels</strong> from this trade — setup type, exit reason, mental state, market condition. These are pulled directly from the fields you filled in at Trade Entry.</LI>
        <LI><strong>Detector context flags</strong> — pattern-based tags like Hour Cliff, Revenge Window, Setup Edge, or Mental State warnings. These come from the detector engine running across your account history.</LI>
      </UL>
      <P>
        Tags also surface on the Dashboard's recent trades list and in
        the Journal.
      </P>

      <Callout kind="warn" title="Detector flags need an active subscription">
        The detector-derived context flags (Hour Cliff, Revenge Window,
        etc.) come from a server-side detector engine that runs across
        your trading history. They populate during your trial and active
        subscription periods. The self-logged labels always work — they
        come directly from your Trade Entry fields.
      </Callout>

      <H2>Execution Scorecard</H2>
      <P>
        Below the summary card is the <UI>Execution Scorecard</UI> — six
        sliders showing how this trade rated on the self-rating
        dimensions you captured in Trade Entry, each with a bar showing
        your personal baseline next to it.
      </P>

      <ScreenshotFrame
        src="/guide/trade-review-scorecard.png"
        alt="Execution Scorecard sliders"
        caption="Each score is plotted against your personal average for that dimension — the small tick mark on the bar shows your baseline."
      />

      <UL>
        <LI><UI>Rule Adherence</UI> — how well you followed your own rules on this trade</LI>
        <LI><UI>Conviction</UI> — how confident you were in the setup</LI>
        <LI><UI>Focus</UI> — how focused your session was</LI>
        <LI><UI>Fear</UI> — fear level during the trade</LI>
        <LI><UI>Impulsiveness</UI> — how reactive vs deliberate you were</LI>
        <LI><UI>Energy</UI> — your physical/mental energy at the time</LI>
      </UL>

      <P>
        Each row shows the rating from this trade (e.g. <UI>5.0</UI>)
        alongside <UI>avg 5.1</UI> — your average for that dimension
        across your full trading history. Bars are color-coded: green
        when above baseline, red when below. The point isn't perfection
        — it's noticing when this trade ran abnormal on any given
        dimension, especially if you're trying to understand a result
        that surprised you.
      </P>

      <Callout kind="tip">
        The Execution Scorecard only renders meaningfully if you've
        filled in the self-rating sliders at the Reflection stage of
        Trade Entry. On a trade where those fields were skipped, the
        scorecard bars are flat — and the Coaching Notes section below
        will show the default "log emotional fields" fallback prompt
        instead of a substantive observation.
      </Callout>

      <H2>Coaching Notes</H2>
      <P>
        Below the scorecard is a <UI>Coaching Notes</UI> block — short
        observations comparing this trade's logged values against your
        own account averages. The notes are deterministic, not AI-
        generated: when a field crosses a threshold (e.g. fear ≥7) or
        drops well below your baseline, the matching note surfaces. If
        nothing crosses a threshold, you'll see a process-focused
        fallback prompt instead.
      </P>
      <P>
        Examples of what coaching notes actually say:
      </P>
      <UL>
        <LI>"Fear was elevated (8/10 vs your avg 4.2) and the trade lost. High-fear entries are a pattern worth watching."</LI>
        <LI>"Impulsiveness was high (8/10). This trade lost — worth reviewing whether the entry met your criteria or was reactive."</LI>
        <LI>"Rule adherence (4/10) was below your average (7.3). Review what rule you bent — consistency here compounds over hundreds of trades."</LI>
        <LI>"You've taken 4 losses in your last 5 trades. This is the revenge-trading window — consider stepping back before the next entry."</LI>
      </UL>
      <P>
        When none of your logged fields cross a threshold on this trade,
        the default note encourages capturing more on the next one:
      </P>
      <UL>
        <LI>"Log emotional fields (fear, focus, impulsiveness, conviction) on your next trade to unlock deeper trade-by-trade analysis."</LI>
      </UL>
      <P>
        Notes are short, specific, and reference your own numbers —
        which is why they need the Reflection-stage fields populated to
        say anything substantive. A trade logged with just direction
        and prices will land on the default fallback every time.
      </P>

      <H2>Your Notes</H2>
      <P>
        At the bottom is a <UI>Your Notes</UI> section showing the free-text
        you wrote during the Reflection stage of Trade Entry. Two fields
        surface here, each with its own formatting:
      </P>
      <UL>
        <LI><strong>Your reflection</strong> — shown in italics with quotes. Comes from the "What did I learn from this trade?" field at Trade Entry.</LI>
        <LI><strong>To improve:</strong> — prefixed in plain text. Comes from the "What would I do differently next time?" field.</LI>
      </UL>
      <P>
        The section only appears if you wrote something in at least one
        of those fields. If you skipped both, Your Notes is hidden — no
        empty placeholder.
      </P>
      <P>
        This is your contemporaneous record of what you were thinking
        right after the trade closed. Useful to read a week or month
        later when patterns become visible that weren't obvious in the
        moment.
      </P>

      <H2>How to use Trade Review</H2>
      <P>
        A few realistic uses:
      </P>
      <UL>
        <LI><strong>After a notable trade</strong> — winning or losing big — use it as a quick post-mortem before the next session</LI>
        <LI><strong>End-of-session retrospective</strong> — review the day's biggest winner and biggest loser to spot patterns</LI>
        <LI><strong>Weekly review</strong> — pick 3–5 trades from the week that surprised you and read each one in detail</LI>
        <LI><strong>Pre-session warmup</strong> — pull up yesterday's last trade to anchor your mental state before today's first</LI>
      </UL>

      <Callout kind="info">
        The <UI>Review Last Trade</UI> action card on the Dashboard
        opens Trade Review directly on your most recent trade — useful
        as a one-click post-trade habit.
      </Callout>
    </GuideLayout>
  );
}
